import { Button, Grid, Typography } from '@mui/material';
import React, { PropsWithChildren, useState } from 'react';

interface Props<FilterType> {
  // filters should be a dictionary-like state object with key-value pairs, as in:
  // const filters = {
  //     field: searchValue
  // }
  filters: FilterType;
  // setFilters should be a useReducer setter of the following form:
  //   const [filters, setFilters] = useReducer(
  //     (curVals: PartsFilters, newVals: any) => ({ ...curVals, ...newVals }),
  //     filtersInitialState
  //   );
  setFilters: React.Dispatch<any>;
  initialFilters: FilterType;
  // name is the key of the dictionary-like filter object, value is a new potential value for that key
  //    after an input change, and filters is the current filter state; the function should return false
  //    if the attempted change is invalid (the form will then remain unchanged), otherwise true
  filterConstraintChecker: (
    name: string,
    value: string,
    filters: FilterType
  ) => boolean;
  // FilterBar will call this function to create its containing fields; the fields should be an array of
  //   JSX element; the onChange prop of each field should be set to handler; each field should have a name prop
  //   set to the appropriate key in the filters dictionary-like object; each field should also have a value set
  //   to the value associated with the appropriate key, as in filters.filterField
  children: (
    handler: (event: React.ChangeEvent<HTMLInputElement>) => void
  ) => JSX.Element[];
  constraintMessage?: string;
}

const FilterBar = <FilterType,>({
  filters,
  setFilters,
  initialFilters,
  filterConstraintChecker,
  children,
  constraintMessage,
}: PropsWithChildren<Props<FilterType>>) => {
  const [inputError, setInputError] = useState<boolean>(false);
  const handleFiltersChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    autocompleteValue?: object,
    autoboxLabel?: string
  ) => {
    const { name, value } = event.target;
    if (!filterConstraintChecker(name, value, filters)) {
      setInputError(true);
      return;
    }
    setInputError(false);
    if (autoboxLabel) {
      setFilters({ [autoboxLabel]: autocompleteValue });
    }
    if (name) {
      setFilters({ [name]: value });
    }
  };
  const clearFilters = () => {
    setFilters(initialFilters);
    setInputError(false);
  };
  return (
    <Grid
      container
      justifyContent='flex-start'
      alignItems='center'
      spacing={1}
      style={{ marginBottom: '.5rem' }}
    >
      {children(handleFiltersChange).map((child, index) => (
        <Grid key={index} item>
          {child}
        </Grid>
      ))}

      <Grid item>
        <Button onClick={clearFilters} variant='contained' color='primary'>
          Clear Filters
        </Button>
      </Grid>
      {inputError && <Typography color='error'>{constraintMessage}</Typography>}
    </Grid>
  );
};

export default FilterBar;
